import { datadogLogs } from "@datadog/browser-logs";
import { CustomerPortalRpcClient } from "@redotech/customer-portal-sdk/rpc/client";
import {
  DraftReturn,
  PickupData,
  ShipmentMethodName,
} from "@redotech/redo-model/draft-return/draft-return";
import { Channel } from "@redotech/redo-model/return";
import { unknownToError } from "@redotech/util/unknown-to-error";
import { useCallback, useContext, useState } from "react";
import { ShippingEstimateContext } from "../../contexts/shipping-estimate";
import {
  getStepsInReturn,
  navigateFromStack,
  Navigator,
  StepInReturn,
} from "../../navigator";
import { CustomerPortalAppSettings } from "../../settings";
import { PACKAGE_PICKUP_UPSELL_METRICS_LOG_STAMP } from "../../util";

const SUBMIT_PICKUP_ERROR_LOG_STAMP = "submitPickupError";
const SUBMIT_PICKUP_SUCCESS_LOG_STAMP = "submitPickupSuccess";
export const usePickupSubmission = (
  client: CustomerPortalRpcClient | undefined,
  returnContext: {
    draftReturn: DraftReturn | undefined;
    setDraftReturn: (draftReturn: DraftReturn | undefined) => void;
  },
  navigation: Navigator,
  settings: CustomerPortalAppSettings | undefined,
  pickupInfo: PickupData,
  channel: Channel,
): { submitPickup: () => Promise<void>; isUpdatingMethod: boolean } => {
  const [isUpdatingMethod, setIsUpdatingMethod] = useState(false);
  const shippingEstimateContext = useContext(ShippingEstimateContext);

  const submitPickup = useCallback(async () => {
    const draftReturn = returnContext.draftReturn;
    if (!client || !draftReturn || !settings) {
      console.error("submitPickup: Missing required dependencies.");
      datadogLogs.logger.error(
        `${PACKAGE_PICKUP_UPSELL_METRICS_LOG_STAMP} ${SUBMIT_PICKUP_ERROR_LOG_STAMP} ${draftReturn?._id} submitPickup: Missing required dependencies.`,
        {
          draftReturn: draftReturn?._id,
          clientDefined: !!client,
          settingsDefined: !!settings,
        },
      );
      return;
    }
    try {
      setIsUpdatingMethod(true);
      const { draftReturn: updatedDraftReturn } =
        await client.setShipmentMethodInfo({
          draftReturnId: draftReturn._id,
          shipmentMethod: {
            method: ShipmentMethodName.PICKUP,
            pickupData: pickupInfo,
          },
        });
      returnContext.setDraftReturn(updatedDraftReturn);
      datadogLogs.logger.info(
        `${PACKAGE_PICKUP_UPSELL_METRICS_LOG_STAMP} ${SUBMIT_PICKUP_SUCCESS_LOG_STAMP} ${draftReturn._id} submitPickup: Successfully updated draft return with pickup info.`,
      );

      navigateFromStack(
        await getStepsInReturn(
          updatedDraftReturn,
          settings,
          () =>
            client.calculateReturnTotals({ draftReturnId: draftReturn._id }),
          channel,
          shippingEstimateContext,
        ),
        StepInReturn.PACKAGE_PICKUP,
        navigation,
      );
    } catch (error) {
      datadogLogs.logger.error(
        `${PACKAGE_PICKUP_UPSELL_METRICS_LOG_STAMP} ${SUBMIT_PICKUP_ERROR_LOG_STAMP} ${draftReturn?._id} submitPickup: Error updating draft return with pickup info.`,
        { draftReturn: draftReturn?._id, pickupInfo },
        unknownToError(error),
      );
    } finally {
      setIsUpdatingMethod(false);
    }
  }, [
    client,
    returnContext,
    settings,
    navigation,
    pickupInfo,
    channel,
    shippingEstimateContext,
  ]);

  return { submitPickup, isUpdatingMethod };
};
